import React from 'react';
import { Edit, Trash2 } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap styles
import { Table } from 'react-bootstrap';

interface TableProps {
  columns: {
    header: string;  // Column header
    field: string;   // Field name in the data object
    render?: (item: any) => React.ReactNode; // Optional custom render function
    onClick?: (item: any) => void;           // Optional click event handler for the cell
  }[]; 
  data: any[]; // Data for the table
  actions?: boolean; // If true, adds action buttons (Edit, Delete) to each row
  onEdit?: (item: any) => void; // Handler for Edit action
  onDelete?: (item: any) => void; // Handler for Delete action
  pagination?: boolean; // Optional: to enable pagination
  paginationData?: {    // Pagination data
    totalElements: number;  // Total number of items in the database
    pageSize: number;       // Number of items per page
    currentPage: number;    // Current page number
  };
  onPageChange?: (pageNumber: number, pageSize: number) => void; 

}

const GenericTable: React.FC<TableProps> = ({ columns, data, actions, onEdit, onDelete, pagination, paginationData, onPageChange }) => {

    // If pagination is enabled, calculate pagination details
    const totalPages = pagination && paginationData ? Math.ceil(paginationData.totalElements / paginationData.pageSize) : 1;
    const currentPage = pagination && paginationData ? paginationData.currentPage : 1;

    const pageSize = paginationData?.pageSize || 20;

    console.log("Page size", totalPages)


  const handleEdit = (item: any) => {
    if (onEdit) onEdit(item);
  };

  const handleDelete = (item: any) => {
    if (onDelete) onDelete(item);
  };

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const renderActions = (item: any) => {
    if (actions) {
      return (
        <td>
          <button className="btn btn-primary btn-sm" onClick={() => handleEdit(item)}>
            <Edit size={16} />
          </button>
          <button className="btn btn-danger btn-sm ms-2" onClick={() => handleDelete(item)}>
            <Trash2 size={16} />
          </button>
        </td>
      );
    }
    return null;
  };

  const handlePageChange = (pageNumber: number) => {
    if (onPageChange) {
      onPageChange(pageNumber, pageSize); // Trigger the page change callback
    }
  };

  const renderPaginationControls = () => {
    const maxVisiblePages = 5; // Maximum number of page numbers to show
    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Generate the array of page numbers to display
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="d-flex flex-column align-items-center mt-3 mb-2">
        <nav aria-label="Page navigation">
          <ul className="pagination mb-0">
            {/* Previous Page Button */}
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => handlePageChange(currentPage - 1)} 
                aria-label="Previous"
                disabled={currentPage === 1}
              >
                <span aria-hidden="true">&laquo;</span>
              </button>
            </li>

            {/* First Page */}
            {startPage > 1 && (
              <>
                <li className="page-item">
                  <button className="page-link" onClick={() => handlePageChange(1)}>
                    1
                  </button>
                </li>
                {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
              </>
            )}

            {/* Page Numbers */}
            {pageNumbers.map((pageNumber) => (
              <li 
                key={pageNumber} 
                className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}
              >
                <button 
                  className="page-link" 
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              </li>
            ))}

            {/* Last Page */}
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                <li className="page-item">
                  <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </button>
                </li>
              </>
            )}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} aria-label="Next">
                <span aria-hidden="true">&raquo;</span>
              </button>
            </li>
          </ul>
        </nav>
        <div className="text-center mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
          <span>Page {currentPage} of {totalPages} ({paginationData?.totalElements} total items)</span>
        </div>
      </div>
    );
  };
  

  return (
    <div className="table-responsive">
      <Table striped bordered hover responsive className="w-100">
        <thead className="table-light">
          <tr>
            {columns?.map((col, index) => (
              <th key={index}>{col.header}</th>
            ))}
            {actions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {data?.length > 0 ? (
            data.map((item, index) => (
              <tr key={index}>
                {columns.map((col, colIndex) => {
                  const cellContent = col.render ? col.render(item) : getNestedValue(item, col.field); // Use render function if available
                  return (
                    <td key={colIndex} onClick={() => col.onClick?.(item)}>
                      {cellContent}
                    </td>
                  );
                })}
                {renderActions(item)}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length + (actions ? 1 : 0)} className="text-center">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Render pagination controls if pagination flag is true */}
      {pagination && renderPaginationControls()}
      
    </div>
  );
};

export default GenericTable;
